import React, { FormEvent, useEffect, useRef, useState } from "react";

import "./styles.scss";
import LazySection from "../../react-only-components/LazySection";
import Pagination from "./components/Pagination";
import SearchFilterDropdown from "./components/SearchFilterDropdown/index";
import Suggestions from "./components/Suggestions";
import RoutableNotEditableLink from "../../react-only-components/RoutableNotEditableLink";
import useQueryParams from "../../hooks/use-query-params.effect";
import { QueryParamsConst } from "../../constants/query-params.constants";
import { SearchApiModel } from "./models/SearchApiModel";
import { TSearchItem, TSearchProps } from "./types/Search.types";
import { windowService, documentService } from "../../utils/window.utils";
import { useTranslation } from "react-i18next";
import {
  Text,
  withSitecoreContext,
  RichText,
} from "@sitecore-jss/sitecore-jss-react";
import { anchorWithId } from "../../helpers/anchorWithId.helper";


const Search = (props: TSearchProps) => {
  const { sitecoreContext } = props;
  const [page, setPage] = useQueryParams(QueryParamsConst.PAGE, "1");
  const [query, setQuery] = useQueryParams(QueryParamsConst.QUERY, "");
  const [perPage] = useQueryParams(QueryParamsConst.PER_PAGE, "10");
  const [filters, setFilters] = useQueryParams(QueryParamsConst.FILTERS, "");
  const [sort, setSort] = useQueryParams(QueryParamsConst.SORT, "");

  const [, setSuggestionQuery] = useState("");
  const [isSuggestionsComponentVisible, setIsSuggestionsComponentVisible] =
    useState(false);

  const SUGGESTIONS_MIN_CHAR_LIMIT = 3;
  const SEPARATOR_CHAR = ",";
  const model = new SearchApiModel(props);
  const pfeInputRef = useRef<any>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const pages = Math.ceil(model.counter / parseInt(perPage, 10));
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof window !== 'undefined' && 'customElements' in window) {
      import('@patternfly/pfe-icon-onhighway/dist/pfe-icon.js');
    }
  }, []);

  const convertFileSize = (sizeInBytes) => {
    if (sizeInBytes) {
      const fielSize = (sizeInBytes / (1024 * 1024)).toFixed(2);
      const fielSizeNumber = parseInt(fielSize);
      if (fielSizeNumber < 1) {
        return (sizeInBytes / 1024).toFixed(2) + " " + " KB";
      } else return (sizeInBytes / (1024 * 1024)).toFixed(2) + " " + "MB";
    }
  };

  const goToNewSearch = () => {
    // debugger;
    const submitUrl = new URL(windowService().location.href.split("?")[0]);
    if (submitUrl && pfeInputRef.current) {
      submitUrl.searchParams.append(
        QueryParamsConst.QUERY,
        pfeInputRef.current?.value
      );
      submitUrl.searchParams.append(QueryParamsConst.PAGE, page);
      submitUrl.searchParams.append(QueryParamsConst.PER_PAGE, perPage);
      filters &&
        submitUrl.searchParams.append(QueryParamsConst.FILTERS, filters);
      sort && submitUrl.searchParams.append(QueryParamsConst.SORT, sort);

      windowService().location.href = submitUrl.toString();
    }
  };

  const onSubmit: (e: FormEvent<HTMLFormElement>) => void = (
    e: FormEvent<HTMLFormElement>
  ) => {
    e?.preventDefault();
    setQuery(pfeInputRef?.current?.value);
    setPage("1");
  };

  const onPaginationChange = (selectedPage: number) =>
    setPage(selectedPage.toString());

  const onFiltersChange = (activeFilters: string[]) => {
    setFilters(activeFilters.join(SEPARATOR_CHAR));
    setPage("1");
  };

  const onKeyUp: (e: React.KeyboardEvent<HTMLFormElement>) => void = (e) => {
    if (e?.key === "Enter") {
      formRef.current?.dispatchEvent(new Event("submit"));
      return;
    }

    updateSuggestionsQuery(e);
  };

  const updateSuggestionsQuery = (event) => {
    setSuggestionQuery(event.target.value);
  };

  const onUpdateQueryFromSuggestions = (q: string) => {
    pfeInputRef.current.value = q;
    setIsSuggestionsComponentVisible(false);
    setQuery(q);
    setPage("1");
  };

  const handleClickOutside = (event) => {
    if (formRef?.current?.contains(event.target)) {
      return;
    }

    setIsSuggestionsComponentVisible(false);
  };

  useEffect(() => goToNewSearch(), [page, query]);

  useEffect(() => {
    if (documentService()) {
      documentService().querySelector("body").style.backgroundColor = "#fff";
      documentService().addEventListener("mouseup", handleClickOutside);

    }
    return () => {
      documentService()?.removeEventListener("mouseup", handleClickOutside);
    };
  }, []);

  return (
    <LazySection>
      {props.fields && (
        <div className="search">
          <div className="search-wrapper">
            <h2 className="search__heading">SEARCH</h2>

            <form onSubmit={onSubmit} ref={formRef}>
              <div className="search-result-page pfe-l-grid pfe-m-gutters">
                <pfe-input
                  name="Search"
                  placeholder={"Search"}
                  type="text"
                  ref={pfeInputRef}
                  class="pfe-m-10-col"
                  value={query}
                  onKeyUp={onKeyUp}
                >
                  <pfe-icon
                    slot="icon-right"
                    icon="cnh-search"
                    size={24}
                    onClick={goToNewSearch}
                  />
                </pfe-input>

                {isSuggestionsComponentVisible && (
                  <Suggestions
                    className="search-suggestions--search-page-input"
                    suggestionQuery={pfeInputRef?.current?.value}
                    language={sitecoreContext?.language || ""}
                    contextId={sitecoreContext?.itemId || ""}
                    minimalLength={SUGGESTIONS_MIN_CHAR_LIMIT}
                    onUpdateQuery={onUpdateQueryFromSuggestions}
                  />
                )}
              </div>
            </form>

            <div className="search_result_container">
              {query !== "" && (
                <p className="search-result-text">
                  {" "}
                  <span className="total-result">{model?.counter}</span> {t("CnhindustrialcomResults")}{" "}
                  {query && <span className="total-result">{query}</span>}
                </p>
              )}
              {
                <div className="dropDownSection">
                  <div className="filterSection dropDownDiv">
                    <SearchFilterDropdown
                      dataVal={model.filter}
                      labelVal={model.Filterlabel}
                      defaultVal={filters}
                      setUpdateVal={setFilters}
                    />
                  </div>
                  <div className="sortSection dropDownDiv">
                    <SearchFilterDropdown
                      dataVal={model.Sort}
                      labelVal={model.Sortlabel}
                      defaultVal={sort}
                      setUpdateVal={setSort}
                    />
                  </div>
                </div>
              }
            </div>

            {query !== "" && (
              <div className="search__results">
                {model?.results?.length ? (
                  <div className="search__results__list">
                    {model?.results?.map((result: TSearchItem, i) => (
                      <article
                        className={"search__item"}
                        key={`search-result-item-${i}`}
                      >
                        <header className="search_result_header">
                          <div className="search_result_header_media">
                            {result.PDFLink ? (
                              <pfe-icon size={24} icon="cnh-attach" />
                            ) : (
                              <pfe-icon size={24} icon="cnh-link" />
                            )}
                            <div className="search_result_header_media-body">
                              <RichText
                                field={{ value: result.title }}
                                tag="h3"
                                className="search_result_header_title"
                              />

                              <RichText
                                field={{
                                  value: result.highlight
                                    ? result.highlight
                                    : result.description,
                                }}
                                tag="div"
                                className="search_result_header_desc"
                              />
                            </div>
                          </div>
                          <div className="links-container">
                            <RoutableNotEditableLink
                              href={anchorWithId(result?.link, result?.anchor)}
                              target="_blank"
                            >
                              <RichText
                                field={{ value: model.ReadMoreText }}
                                tag="span"
                              />
                              <pfe-icon size={24} icon="cnh-arrow-forward" />
                            </RoutableNotEditableLink>
                            {result.PDFLink ? (
                              <div className="file-info">
                                <Text
                                  field={{
                                    value: convertFileSize(result.PDFSize),
                                  }}
                                  tag="span"
                                  className="file-size"
                                />
                                <p>
                                  <a
                                    title={result.AttachmentName}
                                    href={anchorWithId(
                                      result?.PDFLink,
                                      result?.PDFanchor
                                    )}
                                    download
                                  >
                                    <Text
                                      field={{
                                        value: model.Pdflabel,
                                      }}
                                      tag="span"
                                      className="file-extension"
                                    />
                                    <img
                                      src={model.PdfIcon}
                                      alt="pdf-icon"
                                      className="pdf-download-icon"
                                    />
                                  </a>
                                </p>
                              </div>
                            ) : null}
                          </div>
                        </header>
                      </article>
                    ))}
                  </div>
                ) : (
                  <h3 className="search__no-results">
                    {t("CnhindustrialcomNoResults")}
                  </h3>
                )}
              </div>
            )}

            {model?.results?.length > 0 && model?.counter > 10 && (
              <Pagination
                currentPage={parseInt(page, 10)}
                pages={pages}
                goToPage={onPaginationChange}
              />
            )}
          </div>
        </div>
      )}
    </LazySection>
  );
};

export default withSitecoreContext()(Search);
